import { getXPositionByDate } from "@/app/activity/components/timeline/activityTimeline/utils"
import { DataBoxProps } from "@/app/activity/detail/components/dataBox/type"
import { createActivityCommonStore, createSessionStore } from "@/app/activity/store"
import { colors } from "@/components/colors"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import "./dataBox.scss"
import {
  useCreateSessionDataMutation,
  useDeleteSessionDataMutation,
} from "@/hooks/api/useSessionQuery"
import { createGlobalStore } from "@/stores/global"
import { format } from "date-fns"
import { select } from "d3-selection"
import { drag } from "d3-drag"
import { useGetTimePerWidth } from "@/app/activity/components/timeline/activityTimeline/hooks"
import { HOUR_TO_SECOND_FORMAT } from "@/constants/regex"

const dragBoxesWidth = 6
export default function DataBox({
  dateStart,
  dateFinish,
  matchedToSession,
  idfSession,
  idfData,
  cropRecords = [],
  originalDateStart,
  originalDateFinish,
  ...rest
}: DataBoxProps) {
  const leftHandleRef = useRef<SVGCircleElement>(null)
  const rightHandleRef = useRef<SVGCircleElement>(null)
  const saveTimeoutRef = useRef<NodeJS.Timeout>()

  // 드래그 핸들러에서 최신 상태를 참조하기 위한 ref
  const startDateRef = useRef<Date>(new Date(dateStart))
  const endDateRef = useRef<Date>(new Date(dateFinish))
  const { t } = useTranslation()
  const { session, temporarySession, hoveredSession } = createSessionStore((state) => ({
    session: state.session,
    temporarySession: state.temporarySession,
    hoveredSession: state.hoveredSession,
  }))

  const { mutateAsync: createSessionData } = useCreateSessionDataMutation()
  const { mutateAsync: deleteSessionData } = useDeleteSessionDataMutation()

  const { setSnackbar } = createGlobalStore((state) => ({
    setSnackbar: state.setSnackBar,
  }))

  const { range, timelineWidth, remainRightArea } = createActivityCommonStore((state) => ({
    range: state.range,
    timelineWidth: state.timelineWidth,
    remainRightArea: state.remainingArea,
  }))

  const { dateStart: rangeStart, dateFinish: rangeFinish } = range

  const [isMouseOver, setIsMouseOver] = useState(false)
  const [startDate, setStartDate] = useState(new Date(dateStart))
  const [endDate, setEndDate] = useState(new Date(dateFinish))

  // 종료 시간의 upper bound (endDate가 이 값을 넘으면 안 됨)
  // 원본 데이터 종료 시간과 세션 종료 시간 중 더 작은 값
  const endDateUpperBound = useMemo(() => {
    const fromOriginal = new Date(originalDateFinish).valueOf()

    if (temporarySession) {
      const fromSession = new Date(temporarySession.dateFinish).valueOf()

      return new Date(Math.min(fromOriginal, fromSession))
    }

    return new Date(originalDateFinish)
  }, [originalDateFinish, temporarySession])

  // 시작 시간의 lower bound (startDate가 이 값보다 작으면 안 됨)
  // 원본 데이터 시작 시간과 세션 시작 시간 중 더 큰 값
  const startDateLowerBound = useMemo(() => {
    const fromOriginal = new Date(originalDateStart).valueOf()
    if (temporarySession) {
      const fromSession = new Date(temporarySession.dateStart).valueOf()

      return new Date(Math.max(fromOriginal, fromSession))
    }

    return new Date(originalDateStart)
  }, [originalDateStart, temporarySession])

  const timePerWidth = useGetTimePerWidth({
    dateStart: range.dateStart,
    dateFinish: range.dateFinish,
    chartWidth: timelineWidth,
  })

  const startX = useMemo(() => {
    const startXBySession =
      getXPositionByDate(startDate, rangeStart as Date, rangeFinish as Date) * timelineWidth

    if (temporarySession) {
      const startXByTemporarySession =
        getXPositionByDate(
          new Date(temporarySession.dateStart),
          rangeStart as Date,
          rangeFinish as Date,
        ) * timelineWidth

      if (matchedToSession) {
        return Math.max(startXByTemporarySession, startXBySession) + (remainRightArea || 0) / 2
      }

      return startXByTemporarySession + (remainRightArea || 0) / 2
    }

    return startXBySession + (remainRightArea || 0) / 2
  }, [
    rangeStart,
    rangeFinish,
    session,
    temporarySession,
    startDate,
    timelineWidth,
    remainRightArea,
  ])

  const endX = useMemo(() => {
    const endXBySession =
      getXPositionByDate(endDate, rangeStart as Date, rangeFinish as Date) * timelineWidth

    if (temporarySession) {
      const endXByTemporarySession =
        getXPositionByDate(
          new Date(temporarySession.dateFinish),
          rangeStart as Date,
          rangeFinish as Date,
        ) * timelineWidth

      if (matchedToSession) {
        return Math.min(endXByTemporarySession, endXBySession) + (remainRightArea || 0) / 2
      }

      return endXByTemporarySession + (remainRightArea || 0) / 2
    }

    return endXBySession + (remainRightArea || 0) / 2
  }, [endDate, rangeStart, rangeFinish, session, temporarySession, timelineWidth, remainRightArea])

  const boxFill = useMemo(() => {
    if (hoveredSession && hoveredSession.idfSession === idfSession) {
      return colors.blue[500]
    }

    if (hoveredSession && hoveredSession.idfSession !== idfSession) {
      return colors.neutral.grey[200]
    }

    if (matchedToSession) {
      return colors.blue[500]
    }

    return colors.neutral.black
  }, [matchedToSession, hoveredSession, session])

  const boxOpacity = useMemo(() => {
    if (
      (hoveredSession && hoveredSession.idfSession === idfSession && matchedToSession) ||
      (!hoveredSession && matchedToSession)
    ) {
      return 0.3
    }

    return 0
  }, [matchedToSession, hoveredSession, session])

  const matchToSession = async () => {
    const param = { idfSession, idfData }
    const payload = {
      dateStart: startDateRef.current.toISOString(),
      dateFinish: endDateRef.current.toISOString(),
      matchedToSession,
      idfSession,
      idfData,
      ...rest,
    }

    try {
      await createSessionData({ params: param, payload })

      setSnackbar({
        open: true,
        message: t("messages.successUpdate"),
        severity: "success",
      })
    } catch (e) {
      console.error(e)
    }
  }

  const unmatchToSession = async () => {
    const param = { idfSession, idfData }
    const payload = { dateStart, dateFinish, matchedToSession, idfSession, idfData, ...rest }
    try {
      await deleteSessionData({ params: param, payload })

      setSnackbar({
        open: true,
        message: t("messages.successUpdate"),
        severity: "success",
      })
    } catch (e) {
      console.error(e)
    }
  }

  const onClickHandler = () => {
    if (matchedToSession) {
      unmatchToSession()
    } else {
      matchToSession()
    }
  }

  const formatDate = (date: Date) => {
    return format(date, HOUR_TO_SECOND_FORMAT)
  }

  const cropMarks = useMemo(() => {
    return cropRecords.map((crop) => {
      const cropStartX =
        getXPositionByDate(new Date(crop.cropStartTime), rangeStart as Date, rangeFinish as Date) *
          timelineWidth +
        (remainRightArea || 0) / 2

      const cropEndX =
        getXPositionByDate(new Date(crop.cropEndTime), rangeStart as Date, rangeFinish as Date) *
          timelineWidth +
        (remainRightArea || 0) / 2

      return {
        startX: cropStartX,
        endX: cropEndX,
        width: cropEndX - cropStartX,
        ...crop,
      }
    })
  }, [cropRecords, rangeStart, rangeFinish, timelineWidth, remainRightArea])

  const saveChanges = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    saveTimeoutRef.current = setTimeout(() => {
      matchToSession()
    }, 500)
  }

  useEffect(() => {
    const newDate = new Date(dateStart)
    setStartDate(newDate)
    startDateRef.current = newDate
  }, [dateStart])

  useEffect(() => {
    const newDate = new Date(dateFinish)
    setEndDate(newDate)
    endDateRef.current = newDate
  }, [dateFinish])

  useEffect(() => {
    if (leftHandleRef.current) {
      const element = leftHandleRef.current
      select(element as Element).call(
        drag()
          .on("start", function (event) {
            event.sourceEvent.stopPropagation()
            if (saveTimeoutRef.current) {
              clearTimeout(saveTimeoutRef.current)
            }
          })
          .on("drag", (event) => {
            event.sourceEvent.stopPropagation()
            const { x } = event
            const currentStart = startDateRef.current
            const currentEnd = endDateRef.current

            const currentStartX =
              getXPositionByDate(currentStart, rangeStart as Date, rangeFinish as Date) *
                timelineWidth +
              (remainRightArea || 0) / 2
            const currentEndX =
              getXPositionByDate(currentEnd, rangeStart as Date, rangeFinish as Date) *
                timelineWidth +
              (remainRightArea || 0) / 2
            const moveX = x - currentStartX

            if (currentStartX + moveX < currentEndX - 10) {
              let date = new Date(currentStart.getTime() + moveX * timePerWidth)

              // 세션/데이터 시작 시간보다 작아지면 안됨
              if (date.valueOf() < startDateLowerBound.valueOf()) {
                date = startDateLowerBound
              }

              if (date.valueOf() < currentEnd.valueOf()) {
                setStartDate(date)
                startDateRef.current = date
              }
            }
          })
          .on("end", () => {
            saveChanges()
          }),
      )
    }
  }, [leftHandleRef.current, timePerWidth, isMouseOver, matchedToSession])

  useEffect(() => {
    if (rightHandleRef.current) {
      const element = rightHandleRef.current
      select(element as Element).call(
        drag()
          .on("start", function (event) {
            event.sourceEvent.stopPropagation()
            if (saveTimeoutRef.current) {
              clearTimeout(saveTimeoutRef.current)
            }
          })
          .on("drag", (event) => {
            event.sourceEvent.stopPropagation()
            const { x } = event
            const currentStart = startDateRef.current
            const currentEnd = endDateRef.current

            const currentStartX =
              getXPositionByDate(currentStart, rangeStart as Date, rangeFinish as Date) *
                timelineWidth +
              (remainRightArea || 0) / 2
            const currentEndX =
              getXPositionByDate(currentEnd, rangeStart as Date, rangeFinish as Date) *
                timelineWidth +
              (remainRightArea || 0) / 2
            const moveX = x - currentEndX

            if (currentEndX + moveX > currentStartX + 10) {
              let date = new Date(currentEnd.getTime() + moveX * timePerWidth)

              // 세션/데이터 종료 시간보다 커지면 안됨
              if (date.valueOf() > endDateUpperBound.valueOf()) {
                date = endDateUpperBound
              }

              if (date.valueOf() > currentStart.valueOf()) {
                setEndDate(date)
                endDateRef.current = date
              }
            }
          })
          .on("end", () => {
            saveChanges()
          }),
      )
    }
  }, [rightHandleRef.current, timePerWidth, isMouseOver, matchedToSession])

  return !session ? (
    <g>
      <rect
        transform={`translate(${startX},16)`}
        rx="0.2em"
        ry="0.2em"
        height="64"
        width={endX - startX}
        fill={boxFill}
        fillOpacity={boxOpacity}
      />
      {cropMarks.map((crop, index) => (
        <rect
          key={`crop-${index}`}
          transform={`translate(${crop.startX},16)`}
          width={crop.width}
          height="64"
          fill="white"
          fillOpacity={0.5}
          pointerEvents="none"
        />
      ))}
    </g>
  ) : (
    session.idfSession === idfSession && (
      <g
        onClick={onClickHandler}
        onMouseEnter={() => setIsMouseOver(true)}
        onMouseLeave={() => setIsMouseOver(false)}>
        {!matchedToSession && (
          <text
            transform={`translate(${(endX - startX) / 2 + startX},54)`}
            alignmentBaseline="middle"
            fontSize="12"
            textAnchor="middle">
            <tspan>{t("messages.infoParticipateActivity")}</tspan>
          </text>
        )}
        <rect
          className="session-data-box clickable"
          transform={`translate(${startX},16)`}
          rx="0.2em"
          ry="0.2em"
          height="64"
          width={endX - startX}
          fill={matchedToSession ? colors.blue[500] : colors.neutral.black}
          fillOpacity={boxOpacity}
          strokeDasharray={matchedToSession ? "0" : "3 3"}
          stroke={matchedToSession ? colors.neutral.black : colors.blue[500]}
          strokeWidth={matchedToSession ? 0 : 0.7}
        />
        {cropMarks.map((crop, index) => (
          <rect
            key={`crop-${index}`}
            transform={`translate(${crop.startX},16)`}
            width={crop.width}
            height="64"
            fill="white"
            fillOpacity={0.5}
            pointerEvents="none"
          />
        ))}
        {matchedToSession && isMouseOver && (
          <>
            <rect
              width={endX - startX}
              transform={`translate(${startX},76)`}
              height={4}
              rx="0.2em"
              ry="0.2em"
              fill="black"
            />
            {/* 히트 영역 (투명, 더 큰 클릭 영역) */}
            <circle
              className="drag-handle-hit"
              transform={`translate(${startX},10)`}
              cx="0"
              cy="68"
              r="16"
              ref={leftHandleRef}
              onClick={(e) => e.stopPropagation()}
            />
            {/* 보이는 핸들 */}
            <circle
              className="drag-handle"
              transform={`translate(${startX},10)`}
              cx="0"
              cy="68"
              r="7"
            />
            <rect
              transform={`translate(${startX - 53},86)`}
              width={60}
              height={14}
              fill="black"
              rx="3"></rect>
            <text
              transform={`translate(${startX - 23},81)`}
              alignmentBaseline="middle"
              fontSize="12"
              textAnchor="middle">
              <tspan x="0" y="16" fill="white">
                {formatDate(startDate)}
              </tspan>
            </text>
            {/* 히트 영역 (투명, 더 큰 클릭 영역) */}
            <circle
              className="drag-handle-hit"
              transform={`translate(${endX},10)`}
              cx="0"
              cy="68"
              r="16"
              ref={rightHandleRef}
              onClick={(e) => e.stopPropagation()}
            />
            {/* 보이는 핸들 */}
            <circle
              className="drag-handle"
              transform={`translate(${endX},10)`}
              cx="0"
              cy="68"
              r="7"
            />
            <rect
              transform={`translate(${endX - 7},86)`}
              alignmentBaseline="middle"
              fontSize="12"
              width={60}
              height={14}
              fill="black"
              rx="3"></rect>
            <text transform={`translate(${endX + 23},81)`} fontSize="12" textAnchor="middle">
              <tspan x="0" y="16" fill="white">
                {formatDate(endDate)}
              </tspan>
            </text>
          </>
        )}
      </g>
    )
  )
}
